import React, { useEffect, useRef, useState } from 'react';
import 'react-lazy-load-image-component/src/effects/blur.css';
import { Popover } from 'react-tiny-popover';

import InfoSquare from '../../components/InfoSquare';
import { useForcedEventCache } from '../../context/EventCacheContext';
import { useCartCache } from '../../context/CartCacheContext';
import { StepPropsBase } from '../Reserve';
import { groupBy, mapBy, notUndefined } from '../../../../../../../../shared/lib/functional';
import { formatCentsToUSD } from '../../../../../../../../shared/lib/utils';
import Button from '../../components/Button';
import { ContentTag } from '../../components/ContentTag';
import { useApp } from '../../context/AppContext';
import WideImage from '../../components/WideImage';
import Checkbox from '../../components/Checkbox';
import TwoColumnLayout from '../../components/TwoColumnLayout';
import DateCalendar from '../../components/DateCalendar';
import {
  dateToIsoUTC,
  fromDateUTCToDateLocal,
  getLocaleDateStringLocal,
} from '../../../../../../../../shared/lib/time';
import {
  buildCalendarMarkers,
  getSlotSpaceStatus,
} from '../../../../../../../../shared/lib/reserve';
import { evaluateAudienceZone } from '../../../../../../../../shared/lib/audience';

interface ExperienceViewPropsBase {
  selectedDate?: Date;
  onDateChange: (d: Date | undefined) => void;
}

type ExperienceViewProps = ExperienceViewPropsBase & StepPropsBase;

export default function ExperienceView(props: ExperienceViewProps) {
  const eventDiv = useRef<HTMLDivElement>(null);

  const { onNext, selectedDate, onDateChange } = props;
  const { event, allEvents } = useForcedEventCache();
  const { cart, swapDrafts } = useCartCache();
  const { dynamicConstants } = useApp();
  const { isMobile } = dynamicConstants;

  const { eventTemplate, collections, timeslots } = event;

  const segmentsMap = mapBy(collections?.segments ?? [], '_id');
  const attributesMap = mapBy(collections?.attributes ?? [], '_id');

  const [agree, setAgree] = useState(!!cart);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  useEffect(() => {
    if (cart) setAgree(true);
  }, [cart]);

  useEffect(() => {
    if (!selectedDate) {
      const foundDate = timeslots.find(slot => getSlotSpaceStatus(slot).dynamicSpotsLeft > 0)?.date;
      if (foundDate) {
        onDateChange(fromDateUTCToDateLocal(foundDate));
      }
    }
  }, [timeslots, selectedDate]);

  const shortDesc = eventTemplate.description;
  const marketingDesc = eventTemplate.reservables.marketingDescription;
  const marketingPrice = eventTemplate.reservables.marketingPrice;

  const segmentName = segmentsMap[eventTemplate?.segment ?? '']?.name;
  const attributeNames: string[] = eventTemplate.attributes
    .map(attr => attributesMap[attr]?.name)
    .filter(notUndefined);

  const included = eventTemplate.reservables.included ?? [];
  const excluded = eventTemplate.reservables.excluded ?? [];
  const hasIncludedOrExcluded = included.length > 0 || excluded.length > 0;

  const meetingPoint = eventTemplate.reservables.meetingPoint;
  const activityLocation = eventTemplate.reservables.activityLocation;
  const hasLocationSection = !!meetingPoint || !!activityLocation;

  const tickets = eventTemplate.reservables.tickets ?? [];
  const packages = eventTemplate.reservables.packages ?? [];
  const hasTicketsOrPackages = tickets.length > 0 || packages.length > 0;

  const secondaryImages = (eventTemplate.images ?? []).slice(1, 3);

  const duration = eventTemplate.reservables.marketingDuration;
  const occupancy = eventTemplate.reservables.marketingCapacity;
  const hasDurationOrOccupancy = !!duration || !!occupancy;

  const alreadyInCart = cart?.workingDrafts.find(
    d => d.reservationDraft.eventSnapshot.eventTemplate === eventTemplate._id
  );

  function handleOnNext() {
    if (alreadyInCart) {
      swapDrafts(alreadyInCart);
      onNext?.('date');
    } else {
      onNext?.();
    }
  }
  const timeslotsByDate = groupBy(timeslots, t => dateToIsoUTC(t.date));

  const aside = (
    <div className="grid grid-cols-1 gap-6">
      {!isMobile &&
        secondaryImages.map((url, i) => (
          <WideImage key={i} image={url} title={`${eventTemplate.title} image ${i + 2}`} />
        ))}
      <div className="p-6 border border-border rounded-lg grid grid-cols-1 gap-6">
        <div>
          <span className="font-bold text-2xl text-accent">
            {marketingPrice || `From ${formatCentsToUSD(tickets[0]?.price ?? 0)}`}
          </span>
        </div>
        {/* @ts-ignore */}
        <Popover
          isOpen={isCalendarOpen}
          positions={['bottom', 'top']}
          boundaryElement={eventDiv.current || undefined}
          parentElement={eventDiv.current || undefined}
          onClickOutside={() => setIsCalendarOpen(false)}
          containerStyle={{ zIndex: '10' }}
          content={
            <DateCalendar
              selectedDate={selectedDate}
              onDateSelect={d => {
                onDateChange(d);
                setIsCalendarOpen(false);
              }}
              addMarkers={dc => buildCalendarMarkers(dc.dateIso, timeslotsByDate)}
              onClear={() => {
                onDateChange(undefined);
                setIsCalendarOpen(false);
              }}
              className="shadow-lg border border-table"
              showClear
              showToday
            />
          }
        >
          <div>
            <Button
              variant="white"
              size="small"
              block
              onClick={() => setIsCalendarOpen(prev => !prev)}
              className={`border-border-i ${!selectedDate && 'text-muted-i'}`}
            >
              <span className="flex-grow text-left font-normal">
                {selectedDate
                  ? getLocaleDateStringLocal(selectedDate, {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })
                  : 'View availability...'}
              </span>
            </Button>
          </div>
        </Popover>
        <Checkbox
          checked={agree}
          onValue={setAgree}
          disabled={!!cart}
          labelRender={() => (
            <div className="ml-2">
              <span>I agree to the&nbsp;</span>
              <a
                className="text-active underline"
                href="https://drive.google.com/file/d/1FvP_UCw6l_iCe4zB86CjyoE8lOf44sRH/view?usp=sharing"
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms of Service
              </a>
              <span>&nbsp;and to the&nbsp;</span>
              <a
                className="text-active underline"
                href="https://drive.google.com/file/d/1H2VQB7wGcR5dBYqV2-yFjUzec4h3X1s3/view?usp=sharing"
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Policy
              </a>
              .
            </div>
          )}
        />
        <Button disabled={!agree} block onClick={handleOnNext} iconSuffix={['fass', 'arrow-right']}>
          {alreadyInCart ? 'Continue Booking' : 'Book Experience'}
        </Button>
        {eventTemplate.reservables.disclaimer && (
          <div
            className="html-view"
            dangerouslySetInnerHTML={{
              __html: eventTemplate.reservables.disclaimer,
            }}
          />
        )}
      </div>
    </div>
  );

  const prepend = (
    <>
      {shortDesc && <div>{shortDesc}</div>}
      {attributeNames.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap -mb-2">
          {attributeNames.map(name => (
            <ContentTag key={name} variant="light">
              {name}
            </ContentTag>
          ))}
        </div>
      )}
    </>
  );

  const dataCollections = allEvents?.collections ?? {
    segments: [],
    levels: [],
    attributes: [],
    zones: [],
  };

  const visibleZones = dataCollections.zones
    .filter(z => evaluateAudienceZone('visible', z._id, undefined, eventTemplate.audience))
    .map(z => (
      <div
        key={z._id}
        className="text-white shrink-0 min-w-0 bg-black text-sm font-bold rounded-full px-2 py-0 truncate go-event-zone"
        style={{ backgroundColor: z.color }}
      >
        {z.name}
      </div>
    ));

  return (
    <TwoColumnLayout
      ref={eventDiv}
      tags={visibleZones}
      title={segmentName}
      subtitle={eventTemplate.title}
      prepend={prepend}
      aside={aside}
    >
      <WideImage image={eventTemplate.images[0]} title={eventTemplate.title} />
      {hasDurationOrOccupancy && (
        <div className="flex gap-4 flex-wrap">
          {duration && <InfoSquare icon={['fass', 'clock']} label="Duration" subLabel={duration} />}
          {occupancy && (
            <InfoSquare
              icon={['fass', 'user-group-simple']}
              label="Max Guests"
              subLabel={occupancy}
            />
          )}
        </div>
      )}
      {marketingDesc && (
        <>
          <hr className="border-t border-border my-4" />
          <div className="grid grid-cols-1 gap-4">
            <div className="text-xl text-accent font-bold">Details</div>
            <div
              className="html-view"
              dangerouslySetInnerHTML={{
                __html: marketingDesc,
              }}
            ></div>
          </div>
        </>
      )}
      {hasIncludedOrExcluded && (
        <>
          <hr className="border-t border-border my-4" />
          <div className={`grid gap-4 items-start ${isMobile ? '' : 'lg:grid-cols-2'}`}>
            {included.length > 0 && (
              <div className="grid grid-cols-1 gap-4">
                <div className="text-xl text-accent font-bold">What's Included?</div>
                <div className="grid grid-cols-1 gap-2">
                  {included.map((item, i) => (
                    <InfoSquare
                      key={i}
                      icon={['fass', 'check']}
                      label={item.name}
                      variant="green"
                    />
                  ))}
                </div>
              </div>
            )}
            {excluded.length > 0 && (
              <div className="grid grid-cols-1 gap-4">
                <div className="text-xl text-accent font-bold">What's Not Included?</div>
                <div className="grid grid-cols-1 gap-2">
                  {excluded.map((item, i) => (
                    <InfoSquare key={i} icon={['fass', 'xmark']} label={item.name} variant="red" />
                  ))}
                </div>
              </div>
            )}
          </div>
        </>
      )}
      {hasLocationSection && (
        <>
          <hr className="border-t border-border my-4" />
          <div className="text-xl text-accent font-bold">Location</div>
          <div className={`grid grid-cols-1 gap-4 ${hasLocationSection ? 'lg:grid-cols-2' : ''}`}>
            {meetingPoint && (
              <div>
                <InfoSquare
                  icon={['fass', 'location-crosshairs']}
                  label="Meeting Point"
                  subLabel={meetingPoint}
                />
              </div>
            )}

            {activityLocation && (
              <div>
                <InfoSquare
                  icon={['fass', 'location-pin']}
                  label="Activity Location"
                  subLabel={activityLocation}
                />
              </div>
            )}
          </div>
        </>
      )}
      {hasTicketsOrPackages && (
        <>
          <hr className="border-t border-border my-4" />
          <div className="text-xl text-accent font-bold">Prices and Options</div>
          {tickets.length > 0 && (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {tickets.map(ticket => (
                <InfoSquare
                  key={ticket._id}
                  type="image"
                  image={ticket.images[0]}
                  label={ticket.name}
                  subLabel={formatCentsToUSD(ticket.price)}
                />
              ))}
            </div>
          )}
          {packages.length > 0 && (
            <>
              <div className="text-lg font-bold">Packages</div>
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                {packages.map(pkg => (
                  <InfoSquare
                    key={pkg._id}
                    type="image"
                    image={pkg.images[0]}
                    label={pkg.name}
                    subLabel={formatCentsToUSD(pkg.price)}
                  />
                ))}
              </div>
            </>
          )}
        </>
      )}
    </TwoColumnLayout>
  );
}
